import { Link, useLocalSearchParams, type Href } from "expo-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ActivityIndicator, Image, Platform, Pressable, ScrollView, StyleSheet, TextInput, useWindowDimensions, View, type TextStyle } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/alert";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { applyModerationAction, type ModerationActionName } from "@/src/lib/moderation";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { claimModerationCase, getModerationCase, type ModerationCaseDetail, type ModerationProfile } from "@/src/control/api";
import { hasControlPermission } from "@/src/control/permissions";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import {
  controlAccountTypeLabel,
  controlVerificationStatusLabel,
  formatControlDate,
  moderationAccountStatusLabel,
  moderationActionLabel,
  moderationCaseStatusLabel,
  moderationContentStatusLabel,
  moderationReasonLabel,
  moderationReportStatusLabel,
  moderationSeverityLabel,
  moderationSourceLabel,
  moderationTargetLabel,
} from "@/src/control/presentation";

const breakableTextStyle: TextStyle | undefined = Platform.OS === "web"
  ? ({ overflowWrap: "anywhere", wordBreak: "break-word" } as unknown as TextStyle)
  : undefined;

export default function ControlModerationCaseScreen() {
  return <ControlAccessGate permission="moderation.read"><ModerationCase /></ControlAccessGate>;
}

function ModerationCase() {
  const { width } = useWindowDimensions();
  const stackedColumns = width < 900;
  const params = useLocalSearchParams<{ caseId?: string | string[] }>();
  const caseId = useMemo(() => Array.isArray(params.caseId) ? params.caseId[0] : params.caseId || "", [params.caseId]);
  const [data, setData] = useState<ModerationCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const access = useControlAccessContext();
  const canManage = access.status === "allowed" && hasControlPermission(access.member, "moderation.manage");

  useEffect(() => {
    let active = true;
    if (!caseId) {
      setError(true);
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    setError(false);
    getModerationCase(caseId)
      .then((value) => active && setData(value))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, caseId]);

  async function claim() {
    if (!caseId || busy) return;
    setBusy(true);
    try {
      await claimModerationCase(caseId);
      setAttempt((value) => value + 1);
    } catch (cause: unknown) {
      Alert.alert("Prise en charge refusée", friendlyFirebaseError(cause, "Le dossier n’a pas pu être pris en charge."));
    } finally {
      setBusy(false);
    }
  }

  function decide(actionName: ModerationActionName, durationDays?: 1 | 7) {
    if (!caseId || busy || !data || !canManage) return;
    const internalReason = reason.trim();
    if (!internalReason) {
      Alert.alert("Motif requis", "Ajoute un motif interne avant d’appliquer une décision.");
      return;
    }
    const label = moderationActionLabel(actionName, durationDays);
    Alert.alert(
      "Confirmer la décision",
      `${label} sur ce dossier ? Cette action sera enregistrée dans l’audit.`,
      [
        { text: "Annuler", style: "cancel" },
        {
          text: "Confirmer",
          style: actionName === "remove_content" || actionName === "temporary_suspension" ? "destructive" : "default",
          onPress: () => void executeDecision(actionName, internalReason, durationDays),
        },
      ]
    );
  }

  async function executeDecision(actionName: ModerationActionName, internalReason: string, durationDays?: 1 | 7) {
    setBusy(true);
    try {
      await applyModerationAction({ caseId, action: actionName, internalReason, durationDays });
      setReason("");
      setAttempt((value) => value + 1);
    } catch (cause: unknown) {
      Alert.alert("Décision refusée", friendlyFirebaseError(cause, "La décision n’a pas pu être enregistrée."));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <State title="Chargement du dossier…"><ActivityIndicator color={text.brand} /></State>;
  if (error || !data) return <State title="Dossier indisponible"><Pressable accessibilityRole="button" onPress={() => setAttempt((value) => value + 1)}><Text style={styles.link}>Réessayer</Text></Pressable></State>;

  const item = data.case;
  const closed = item.status === "resolved" || item.status === "dismissed";
  const primaryReason = item.reason || item.autoModerationResult?.reasonCodes?.[0];
  return (
    <ScrollView contentContainerStyle={styles.page}>
      <Link href={"/control/moderation" as Href} asChild>
        <Pressable accessibilityRole="link"><Text style={styles.back}>‹ Retour à la file</Text></Pressable>
      </Link>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.kicker}>DOSSIER DE MODÉRATION</Text>
          <Text style={[styles.title, breakableTextStyle]}>{moderationReasonLabel(primaryReason)}</Text>
          <Text style={[styles.subtitle, breakableTextStyle]}>{moderationTargetLabel(item.targetType, item.reportedIdentityType)} · créé le {formatControlDate(item.createdAt)} · mis à jour le {formatControlDate(item.updatedAt)}</Text>
        </View>
        <StatusBadge status={item.status} />
      </View>

      <View style={[styles.columns, stackedColumns && styles.columnsStack]}>
        <View style={[styles.mainColumn, stackedColumns && styles.stackedColumn]}>
          <Section title="Contenu signalé">
            <ContentPreview content={data.content} />
            <ControlDisclosure>
              <ControlId label="ID du dossier" value={item.id} />
              <ControlId label="ID de la cible" value={item.targetId} />
              <ControlId label="ID de la publication" value={item.postId} />
            </ControlDisclosure>
          </Section>
          <Section title="Auteur">
            <ProfileBlock profile={data.author} fallbackUid={item.reportedUserUid} />
            {data.moderationState ? (
              <View style={styles.keyValues}>
                <ControlKeyValue label="État du compte" value={moderationAccountStatusLabel(data.moderationState.accountStatus)} />
                {data.moderationState.sanctionType ? <ControlKeyValue label="Mesure en cours" value={moderationActionLabel(data.moderationState.sanctionType)} /> : null}
                {data.moderationState.expiresAt ? <ControlKeyValue label="Expiration" value={formatControlDate(data.moderationState.expiresAt)} /> : null}
              </View>
            ) : null}
          </Section>
          <Section title="Signalements">
            {!data.reports.length ? <Text style={styles.muted}>Aucun signalement utilisateur associé.</Text> : data.reports.map((report) => (
              <View key={report.id} style={styles.historyItem}>
                <View style={styles.historyHeader}><Text style={[styles.historyTitle, breakableTextStyle]}>{moderationReasonLabel(report.reason)}</Text><Text style={styles.smallBadge}>{moderationReportStatusLabel(report.status)}</Text></View>
                <Text style={[styles.muted, breakableTextStyle]}>Signalé par {profileLabel(report.reporter)} · {formatControlDate(report.createdAt)}</Text>
                {report.severity ? <Text style={styles.muted}>Sévérité : {moderationSeverityLabel(report.severity)}</Text> : null}
                {report.details ? <Text style={[styles.body, breakableTextStyle]}>{report.details}</Text> : null}
                <ControlDisclosure title="Détails du signalement">
                  <ControlId label="ID du signalement" value={report.id} />
                  <ControlId label="UID du reporter" value={report.reporterUid} />
                </ControlDisclosure>
              </View>
            ))}
          </Section>
          <Section title="Historique des décisions">
            {!data.actions.length ? <Text style={styles.muted}>Aucune décision enregistrée.</Text> : data.actions.map((entry) => (
              <View key={entry.id} style={styles.historyItem}>
                <View style={styles.historyHeader}><Text style={[styles.historyTitle, breakableTextStyle]}>{moderationActionLabel(entry.action, entry.type === "temporary_restriction" || entry.type === "suspension" ? 1 : undefined)}</Text><Text style={styles.smallBadge}>{moderationReportStatusLabel(entry.status || "recorded")}</Text></View>
                <Text style={[styles.muted, breakableTextStyle]}>Acteur Control · {formatControlDate(entry.createdAt)}</Text>
                {entry.reason || entry.internalReason ? <Text style={[styles.body, breakableTextStyle]}>{entry.reason || entry.internalReason}</Text> : null}
                {entry.expiresAt ? <Text style={styles.muted}>Expire le {formatControlDate(entry.expiresAt)}</Text> : null}
                <ControlDisclosure title="Détails de la décision">
                  <ControlId label="ID de l’action" value={entry.id} />
                  <ControlId label="UID de l’acteur" value={entry.moderatorUid || entry.createdBy} />
                </ControlDisclosure>
              </View>
            ))}
          </Section>
        </View>

        <View style={[styles.sideColumn, stackedColumns && styles.stackedColumn]}>
          <Section title="Décision">
            {closed ? (
              <Text style={styles.muted}>Ce dossier est {moderationCaseStatusLabel(item.status).toLowerCase()}. Aucune nouvelle décision n’est disponible.</Text>
            ) : canManage ? (
              <>
                <Text style={styles.note}>Ajoute un motif interne avant de confirmer une action.</Text>
                <TextInput
                  accessibilityLabel="Motif interne de la décision"
                  editable={!busy}
                  multiline
                  onChangeText={setReason}
                  placeholder="Motif interne obligatoire"
                  placeholderTextColor={text.muted}
                  style={styles.reasonInput}
                  value={reason}
                />
                {item.status === "open" ? <DecisionButton busy={busy} label="Prendre en charge" onPress={() => void claim()} /> : null}
                {data.content?.type === "post" ? <>
                  <Text style={styles.groupLabel}>Contenu</Text>
                  <DecisionButton danger busy={busy} label="Retirer le contenu" onPress={() => decide("remove_content")} />
                </> : null}
                <Text style={styles.groupLabel}>Dossier</Text>
                <DecisionButton busy={busy} label="Classer sans suite" onPress={() => decide("dismiss")} />
                <Text style={styles.groupLabel}>Auteur</Text>
                <DecisionButton busy={busy} label="Avertissement" onPress={() => decide("warning")} />
                <DecisionButton busy={busy} label="Restriction temporaire · 1 jour" onPress={() => decide("posting_restricted", 1)} />
                <DecisionButton busy={busy} label="Restriction temporaire · 7 jours" onPress={() => decide("posting_restricted", 7)} />
                <DecisionButton danger busy={busy} label="Suspension · 1 jour" onPress={() => decide("temporary_suspension", 1)} />
                <DecisionButton danger busy={busy} label="Suspension · 7 jours" onPress={() => decide("temporary_suspension", 7)} />
              </>
            ) : <Text style={styles.muted}>Tu peux consulter ce dossier, mais tu n’as pas la permission de décider.</Text>}
          </Section>
          <Section title="Résumé">
            <View style={styles.keyValues}>
              <ControlKeyValue label="Raison principale" value={moderationReasonLabel(primaryReason)} />
              <ControlKeyValue label="Sévérité" value={moderationSeverityLabel(item.severity)} />
              <ControlKeyValue label="Source" value={moderationSourceLabel(item.source)} />
              <ControlKeyValue label="Signalements" value={`${item.reportCount ?? 0} · ${item.uniqueReporterCount ?? 0} reporter${item.uniqueReporterCount === 1 ? "" : "s"} unique${item.uniqueReporterCount === 1 ? "" : "s"}`} />
            </View>
            <ControlDisclosure>
              <ControlId label="ID du dossier" value={item.id} />
              <ControlId label="ID de la cible" value={item.targetId} />
            </ControlDisclosure>
          </Section>
        </View>
      </View>
    </ScrollView>
  );
}

function ContentPreview({ content }: { content: ModerationCaseDetail["content"] }) {
  if (!content) return <Text style={styles.muted}>Le contenu n’est plus disponible.</Text>;
  if (content.type === "post" && content.post) {
    return <View style={styles.content}>
      <Text style={[styles.contentMeta, breakableTextStyle]}>{content.post.subject || "Publication"} · {content.post.visibility === "group" ? content.post.groupName || "Groupe" : "Espace public"}</Text>
      <Text style={[styles.body, breakableTextStyle]}>{content.post.text || "Publication sans texte."}</Text>
      {content.post.images?.length ? <ScrollView horizontal contentContainerStyle={styles.images} showsHorizontalScrollIndicator={false}>{content.post.images.map((image, index) => <Image key={`${image.url}-${index}`} source={{ uri: image.url }} style={styles.image} />)}</ScrollView> : null}
      {content.post.attachmentName ? <Text style={styles.muted}>Pièce jointe : {content.post.attachmentName}</Text> : null}
      <Text style={styles.muted}>État du contenu : {moderationContentStatusLabel(content.post.moderationStatus)}</Text>
    </View>;
  }
  if (content.type === "comment" && content.comment) return <View style={styles.content}><Text style={[styles.body, breakableTextStyle]}>{content.comment.text || "Commentaire sans texte."}</Text><Text style={styles.muted}>Commentaire · {formatControlDate(content.comment.createdAt)}</Text></View>;
  if ((content.type === "direct_message" || content.type === "group_message") && content.message) {
    const groupMessage = content.type === "group_message";
    return <View style={styles.content}>
      <Text style={[styles.contentMeta, breakableTextStyle]}>
        {groupMessage ? "Message de groupe" : "Message direct"} · {groupMessage ? content.groupId || content.conversationId || "Groupe" : content.conversationId || "Conversation"}
      </Text>
      <Text style={[styles.body, breakableTextStyle]}>{content.message.text || "Message sans texte."}</Text>
      <Text style={styles.muted}>Message {content.messageId || ""} · identité émettrice : {content.message.senderIdentityId}</Text>
    </View>;
  }
  if (content.type === "user" && content.profile) return <ProfileBlock profile={content.profile} />;
  return <Text style={styles.muted}>Le contenu n’est plus disponible.</Text>;
}

function ProfileBlock({ profile, fallbackUid }: { profile?: ModerationProfile | null; fallbackUid?: string }) {
  const uid = profile?.uid || fallbackUid;
  const name = profile?.displayName || (profile?.username ? `@${profile.username.replace(/^@/, "")}` : "Auteur non renseigné");
  return <View style={styles.profile}>
    {profile?.photoURL ? <Image source={{ uri: profile.photoURL }} style={styles.profileAvatar} accessibilityLabel={`Avatar de ${name}`} /> : null}
    <Text style={[styles.profileName, breakableTextStyle]}>{name}</Text>
    {profile?.username ? <Text style={styles.handle}>@{profile.username.replace(/^@/, "")}</Text> : null}
    {profile?.institutionName ? <Text style={[styles.muted, breakableTextStyle]}>{profile.institutionName}</Text> : null}
    {profile?.badges?.length ? <Text style={[styles.muted, breakableTextStyle]}>{profile.badges.map((badge) => badge.label).join(" · ")}</Text> : null}
    <View style={styles.keyValues}>
      <ControlKeyValue label="Type de compte" value={controlAccountTypeLabel(profile?.accountType || profile?.role)} />
      <ControlKeyValue label="Vérification" value={controlVerificationStatusLabel(profile?.verificationStatus)} />
    </View>
    <ControlDisclosure>
      <ControlId label={profile?.identityId ? "ID de l’identité" : "UID de l’auteur"} value={profile?.identityId || uid} />
    </ControlDisclosure>
  </View>;
}

function DecisionButton({ label, onPress, busy, danger = false }: { label: string; onPress: () => void; busy: boolean; danger?: boolean }) {
  return <Pressable accessibilityRole="button" accessibilityState={{ busy, disabled: busy }} disabled={busy} onPress={onPress} style={[styles.decisionButton, !danger && styles.decisionButtonPrimary, danger && styles.decisionButtonDanger, busy && styles.disabled]}><Text style={danger ? styles.dangerText : styles.decisionText}>{busy ? "Traitement…" : label}</Text></Pressable>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <View style={styles.section}><Text style={styles.sectionTitle}>{title}</Text>{children}</View>;
}

function State({ title, children }: { title: string; children?: ReactNode }) {
  return <View style={styles.state}><Text style={styles.stateTitle}>{title}</Text>{children}</View>;
}

function StatusBadge({ status }: { status: "open" | "reviewing" | "resolved" | "dismissed" }) {
  return <Text style={[styles.badge, status === "dismissed" ? styles.badgeMuted : status === "resolved" ? styles.badgeSuccess : styles.badgeActive]}>{moderationCaseStatusLabel(status)}</Text>;
}

function profileLabel(profile?: { username?: string; displayName?: string } | null) {
  return profile?.displayName || (profile?.username ? `@${profile.username.replace(/^@/, "")}` : "Reporter non renseigné");
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  back: { color: text.brand, fontWeight: "800" },
  header: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  headerText: { flex: 1, flexShrink: 1, gap: spacing.sm, minWidth: 0 },
  kicker: { color: text.brand, fontSize: 11, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 27, fontWeight: "800", minWidth: 0 },
  subtitle: { color: text.secondary, lineHeight: 21, minWidth: 0 },
  columns: { flexDirection: "row", flexWrap: "wrap", gap: spacing.lg },
  columnsStack: { flexDirection: "column" },
  mainColumn: { flexBasis: 0, flexGrow: 2, flexShrink: 1, gap: spacing.lg, minWidth: 0 },
  sideColumn: { flexBasis: 0, flexGrow: 1, flexShrink: 1, gap: spacing.lg, minWidth: 0 },
  stackedColumn: { flexBasis: "auto", flexGrow: 0, width: "100%" },
  section: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  sectionTitle: { color: text.primary, fontSize: 17, fontWeight: "800", minWidth: 0 },
  content: { gap: spacing.sm, minWidth: 0 },
  contentMeta: { color: text.brand, fontWeight: "700", minWidth: 0 },
  body: { color: text.primary, lineHeight: 22, minWidth: 0 },
  note: { color: text.secondary, lineHeight: 21, minWidth: 0 },
  muted: { color: text.muted, lineHeight: 20, minWidth: 0 },
  keyValues: { gap: spacing.md, minWidth: 0 },
  profile: { gap: spacing.md, minWidth: 0 },
  profileAvatar: { borderColor: border.brand, borderRadius: 999, borderWidth: 1, height: 56, width: 56 },
  profileName: { color: text.primary, fontSize: 16, fontWeight: "800", minWidth: 0 },
  handle: { color: text.secondary, fontSize: 13 },
  images: { gap: spacing.sm },
  image: { backgroundColor: surface.elevated, borderRadius: radius.sm, height: 180, width: 180 },
  historyItem: { borderColor: border.subtle, borderTopWidth: 1, gap: spacing.xs, minWidth: 0, paddingTop: spacing.md },
  historyHeader: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "space-between", minWidth: 0 },
  historyTitle: { color: text.primary, flex: 1, flexShrink: 1, fontWeight: "800", minWidth: 0 },
  smallBadge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.muted, fontSize: 11, overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  reasonInput: { borderColor: border.strong, borderRadius: radius.sm, borderWidth: 1, color: text.primary, minHeight: 90, minWidth: 0, padding: spacing.md, textAlignVertical: "top", width: "100%" },
  groupLabel: { color: text.secondary, fontSize: 12, fontWeight: "800", marginTop: spacing.sm },
  decisionButton: { alignItems: "center", borderRadius: radius.sm, borderWidth: 1, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  decisionButtonPrimary: { backgroundColor: surface.brand, borderColor: surface.brand },
  decisionButtonDanger: { backgroundColor: surface.danger, borderColor: border.danger },
  decisionText: { color: text.inverse, fontWeight: "800", textAlign: "center" },
  dangerText: { color: text.danger, fontWeight: "800", textAlign: "center" },
  disabled: { opacity: 0.5 },
  badge: { borderRadius: radius.pill, fontSize: 12, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  badgeActive: { backgroundColor: surface.warning, color: text.brand },
  badgeSuccess: { backgroundColor: surface.success, color: text.info },
  badgeMuted: { backgroundColor: surface.selected, color: text.muted },
  state: { alignItems: "center", backgroundColor: surface.secondary, flex: 1, gap: spacing.md, justifyContent: "center", padding: spacing.xl },
  stateTitle: { color: text.primary, fontSize: 20, fontWeight: "800" },
  link: { color: text.brand, fontWeight: "800" },
});
